import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import HeadingLayout from "src/components/base/heading-layout";
import { ApiInternal } from "src/api/api_internal";
import WS from "src/api/ws";
import { IdentificationIcon, TrashIcon } from "@heroicons/react/20/solid";
import { EmptyState } from "src/components/base/empty-state";
import { Button, PrimaryButton } from "src/components/base/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { TenantBadge } from "src/components/badges/tenant";

/**
 * The properties for {@link TenantOverview}
 */
export type TenantOverviewProps = {};

/**
 * The overview page for tenants
 */
export default function TenantOverview(props: TenantOverviewProps) {
    const [t] = useTranslation("tenant");
    const [tg] = useTranslation();

    const navigate = useNavigate();
    const router = useRouter();

    const data = Route.useLoaderData();

    useEffect(() => {
        const listener = WS.addEventListener("message.TenantChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    return (
        <HeadingLayout
            heading={t("heading.tenant-overview")}
            headingChildren={
                <PrimaryButton onClick={async () => await navigate({ to: "/a/tenants/create" })}>
                    {t("button.create-tenant")}
                </PrimaryButton>
            }
        >
            {data.list.length === 0 ? (
                <EmptyState
                    icon={<IdentificationIcon />}
                    title={tg("empty.no-results-title")}
                    description={tg("empty.no-results-description")}
                    action={
                        <PrimaryButton onClick={() => {}}>
                            <IdentificationIcon />
                            {t("button.create-tenant")}
                        </PrimaryButton>
                    }
                />
            ) : (
                <Table dense={true}>
                    <TableHead>
                        <TableRow>
                            <TableHeader></TableHeader>
                            <TableHeader>{t("label.tenant-name")}</TableHeader>
                            <TableHeader>{t("label.tenant-description")}</TableHeader>
                            <TableHeader className={"w-0"}>
                                <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                            </TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.list.map((tenant) => (
                            <TableRow
                                key={tenant.uuid}
                                href={"/a/tenants/$tenantId/general"}
                                params={{ tenantId: tenant.uuid }}
                            >
                                <TableCell>
                                    <TenantBadge tenant={tenant} />
                                </TableCell>
                                <TableCell>{tenant.name}</TableCell>
                                <TableCell>{tenant.description}</TableCell>
                                <TableCell>
                                    <Button
                                        plain={true}
                                        onClick={() =>
                                            navigate({
                                                to: "/a/tenants/$tenantId/delete-preview",
                                                params: { tenantId: tenant.uuid },
                                            })
                                        }
                                    >
                                        <TrashIcon />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </HeadingLayout>
    );
}

export const Route = createFileRoute("/_admin/a/tenants/")({
    component: TenantOverview,
    loader: async () => await ApiInternal.tenants.getAll(),
});
